import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Text,
  Flex,
  Badge,
  Icon,
  useColorModeValue,
  useOutsideClick,
  Portal,
} from '@chakra-ui/react';
import { FiChevronDown, FiPlus, FiCheck } from 'react-icons/fi';

/**
 * A select dropdown that shows existing options with optional color badges,
 * supports type-to-filter, and offers a "Create new" option at the bottom.
 *
 * Props:
 *   options     — [{ value, label, color? }]
 *   value       — current selected value (string)
 *   onChange    — (value: string) => void
 *   placeholder — input placeholder
 *   size        — Chakra size ('sm' | 'md')
 *   allowCreate — show "Create: xyz" option when typed value doesn't match (default true)
 */
export default function CreatableSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select or type...',
  size = 'sm',
  allowCreate = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const bg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  const selectedOption = options.find((o) => o.value === value);
  const displayValue = isOpen ? search : (selectedOption?.label || value || '');

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
    );
  }, [options, search]);

  const exactMatch = options.some(
    (o) => o.value.toLowerCase() === search.toLowerCase() || o.label.toLowerCase() === search.toLowerCase()
  );
  const showCreate = allowCreate && search.trim() && !exactMatch;

  const handleSelect = (val) => {
    onChange(val);
    setSearch('');
    setIsOpen(false);
  };

  const handleCreate = () => {
    const newValue = search.trim().toLowerCase().replace(/\s+/g, '-');
    onChange(newValue);
    setSearch('');
    setIsOpen(false);
  };

  const handleFocus = () => {
    setIsOpen(true);
    setSearch('');
  };

  const handleInputChange = (e) => {
    setSearch(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <Box position="relative" ref={containerRef}>
      <InputGroup size={size}>
        <Input
          ref={inputRef}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          pr="2rem"
        />
        <InputRightElement
          cursor="pointer"
          onClick={() => { setIsOpen(!isOpen); inputRef.current?.focus(); }}
        >
          <Icon as={FiChevronDown} color="gray.400" boxSize={3.5} />
        </InputRightElement>
      </InputGroup>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          shadow="lg"
          zIndex={1500}
          maxH="220px"
          overflowY="auto"
        >
          <List spacing={0}>
            {value && (
              <ListItem
                px={3}
                py={1.5}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={() => handleSelect('')}
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize={size === 'sm' ? 'sm' : 'md'} color="gray.400" fontStyle="italic">
                  Clear selection
                </Text>
              </ListItem>
            )}
            {filtered.map((o) => {
              const isSelected = o.value === value;
              return (
                <ListItem
                  key={o.value}
                  px={3}
                  py={1.5}
                  cursor="pointer"
                  bg={isSelected ? selectedBg : 'transparent'}
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleSelect(o.value)}
                >
                  <Flex align="center" gap={2}>
                    {o.color && (
                      <Badge
                        colorScheme={o.color}
                        fontSize="9px"
                        borderRadius="full"
                        px={1.5}
                      >
                        {'\u00A0'}
                      </Badge>
                    )}
                    <Text fontSize={size === 'sm' ? 'sm' : 'md'}>{o.label}</Text>
                    {isSelected && (
                      <Icon as={FiCheck} color="blue.500" ml="auto" boxSize={3.5} />
                    )}
                  </Flex>
                </ListItem>
              );
            })}

            {filtered.length === 0 && !showCreate && (
              <ListItem px={3} py={2}>
                <Text fontSize="sm" color="gray.400">No options found</Text>
              </ListItem>
            )}

            {showCreate && (
              <ListItem
                px={3}
                py={1.5}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={handleCreate}
                borderTop="1px solid"
                borderColor={borderColor}
              >
                <Flex align="center" gap={2}>
                  <Icon as={FiPlus} color="blue.500" boxSize={3.5} />
                  <Text fontSize="sm" color="blue.500" fontWeight="medium">
                    Create "{search.trim()}"
                  </Text>
                </Flex>
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </Box>
  );
}
